import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";

import { RecepcionesEdit } from '../recepciones-edit/recepciones-edit';
import { RecepcionesService } from '../../Service/recepciones-service';
import { ToastService } from '../../Service/toast-service';
import { AuthService } from '../../Service/auth';
import { Router } from '@angular/router';
import { SignatureDialog } from '../signature-dialog/signature-dialog';

export interface RecepcionGrid {
  id: number;
  folio: string;
  fecha_entrada: string;
  kilometraje: number;
  nivelCombustible: string;
  observaciones_generales: string;
  idAsesor: { an8: number; nameAlpha?: string };
  customer: { an8: number; alph?: string };
  unidad: { id: number; placas: string; submarca: string };
  detallesCarroceria: any[];
}

@Component({
  selector: 'app-recepciones-list',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, FormsModule],
  templateUrl: './recepciones-list.html',
  styleUrl: './recepciones-list.css',
})
export class RecepcionesList implements OnInit {
  recepciones: RecepcionGrid[] = [];
  recepcionesFiltradas: RecepcionGrid[] = [];
  filtroTexto: string = '';

  // =========================================================================
  // 🎛️ VARIABLES CORE PARA TU NUEVA PAGINACIÓN REACTIVA DE RECEPCIONES
  // =========================================================================
  paginaActual: number = 1;
  registrosPorPagina: number = 10; // Límite exacto de renglones por rejilla
  totalPaginas: number = 1;

  // Hacemos visible la librería Math para que la plantilla HTML no arroje errores ts(2339)
  protected readonly Math = Math;

  constructor(
    private recepcionesService: RecepcionesService,
    public dialog: MatDialog,
    public toastService: ToastService,
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // 🛡️ RECEPTOR DE ESTADOS ASÍNCRONOS RECEPCIONES (CICLO SEGURO CSR - INTACTO)
    const estadoRuta = typeof window !== 'undefined' ? history.state : null;

    if (estadoRuta && estadoRuta['mostrarAlertaExito'] === true) {
      console.log('✅ [SaaS Recepciones List] Desplegando Toast de guardado real legítimo.');
      
      // Desplegamos el Toast verde real inyectado por el onSave()
      this.toastService.showSuccess('Recepción Registrada', estadoRuta['mensajeToast']);
      
      // =========================================================================
      // 🛡️ REESCRITURA NATIVA DEL HISTORIAL DE RED (ELIMINA EL TOAST FANTASMA DEFINITIVO)
      // SOLUCIÓN: Usamos window.history.replaceState para limpiar el estado de la URL de Chrome.
      // Esto desintegra la propiedad congelada de la memoria RAM del navegador en caliente.
      // Si el asesor entra a editar y presiona Cancelar, no existirá ninguna alerta vieja.
      // =========================================================================
      if (typeof window !== 'undefined' && window.history) {
        window.history.replaceState({}, document.title, window.location.pathname);
        console.log('🧹 [SaaS Recepciones List] Memoria intermedia de la URL purgada en Chrome.');
      }
      
      this.cdr.detectChanges(); // Fuerza a Angular a pintar el Toast de inmediato
    }    
    
    this.cargarRecepciones(); // Tu consulta tradicional que recarga la tabla de fondo
  }

  /**
   * 🔄 REPETICIÓN DE RENDIMIENTO EN VIVO:
   * Sincroniza la descarga de órdenes mitigando el choque de firmas ts(2769).
   */
  cargarRecepciones(): void {
    console.log('>>> [ERP JDE] Solicitando historial de órdenes de recepción real...');
    
    // 🚀 FIX: Quitamos el tipado estricto del canal para que acepte la respuesta del servicio
    this.recepcionesService.getRecepcionesList().subscribe({
      next: (data: any[]) => {
        console.log('>>> [ERP JDE] Datos crudos de órdenes recibidos:', data);

        // Mapeamos de forma segura cada orden para transformarla legalmente a tu interfaz RecepcionGrid
        this.recepciones = data.map((r: any) => {

          // 🔍 LOGS DE CONTROL: Imprime el AN8 del asesor y del cliente de cada registro crudo
          console.log(`>>> [AN8 Check] Folio: ${r.folio} | AN8 Asesor:`, r.idAsesor?.an8 || r.id_asesor);
          console.log(`>>> [AN8 Check] Folio: ${r.folio} | AN8 Cliente:`, r.customer?.an8 || r.id_cliente);

          // Retornamos el objeto mapeado
          return { 

            id: r.id,
            folio: r.folio || 'S/F',
            fecha_entrada: r.fecha_entrada || r.fechaAlta || '',
            kilometraje: r.kilometraje || 0,
            nivelCombustible: r.nivelCombustible || r.combustible || 'VACIO',
            observaciones_generales: r.observaciones_generales || r.observaciones || '',
            
            // Mapeos relacionales robustos con candados contra nulos
            idAsesor: { 
              an8: r.idAsesor?.an8 || r.id_asesor || 0, 
              nameAlpha: r.idAsesor?.nameAlpha || 'ASESOR NO ASIGNADO' 
            },
            customer: { 
              an8: r.customer?.an8 || r.id_cliente || 0, 
              alph: r.customer?.nameAlpha || r.customer?.alph || 'CLIENTE COMPAÑÍA' 
            },
            unidad: { 
              id: r.unidad?.id || 0, 
              placas: r.unidad?.placas || 'S/P', 
              submarca: r.unidad?.submarca || 'VEHÍCULO' 
            },
            detallesCarroceria: r.detallesCarroceria || [],
            estatus: r.estatus || 'PENDIENTE_FIRMA' // 🚀 CONECTADO: Mapeo de flujo en vivo
          };
        });

        // 💡 POBLADO INICIAL AUTOMÁTICO: Llenamos el array del buscador reactivo en caliente
        this.recepcionesFiltradas = [...this.recepciones];

        console.log('>>> [ERP JDE] Grid de recepciones unificado con éxito. Registros:', this.recepciones.length);
        
        // Rompemos el congelamiento de la interfaz forzando el redibujado de las celdas
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error crítico al descargar recepciones de la DB:', err);
        this.toastService.showDanger(
          'Fallo de Sincronización', 
          'No se pudo conectar con el módulo de órdenes. Verifique el puerto 8085.'
        );
        this.cdr.detectChanges();
      }
    });
  }

  filtrarRecepciones(): void {
    const busqueda = this.filtroTexto.trim().toUpperCase();
    if (!busqueda) {
      this.recepcionesFiltradas = [...this.recepciones];
      this.cdr.detectChanges();
      return;
    }

    this.recepcionesFiltradas = this.recepciones.filter(r => {
      return (r.folio && r.folio.toUpperCase().includes(busqueda)) ||
             (r.unidad?.placas && r.unidad.placas.toUpperCase().includes(busqueda)) ||
             (r.unidad?.submarca && r.unidad.submarca.toUpperCase().includes(busqueda)) ||
             (r.customer?.an8 && r.customer.an8.toString().includes(busqueda));
    });

    this.paginaActual = 1;
    this.calcularTotalPaginas();

    this.cdr.detectChanges();
  }

  // =========================================================================
  // 🚀 REDIRECCIÓN FLUIDA DE PÁGINA COMPLETA (ELIMINA EL TOAST FANTASMA AL CANCELAR)
  // SOLUCIÓN: Removemos el listener del diálogo. Ahora el listado abre la URL limpia.
  // Si el usuario presiona Cancelar, regresa al listado en absoluto silencio UX.
  // Si presiona Guardar, la ruta inyecta el 'state' controlado que enciende el Toast legal.
  // =========================================================================
  abrirEditor(orden: any): void {
    console.log('🚀 [SaaS Recepciones] Redireccionando a Pantalla Completa de Edición para ID:', orden.id);
    this.router.navigate(['/recepciones/edit', orden.id]);
  }

  calcularTotalPaginas(): void {
    // Evitamos divisiones por cero si el filtro de búsqueda no arroja registros
    this.totalPaginas = Math.ceil(this.recepcionesFiltradas.length / this.registrosPorPagina) || 1; // 💡 Ajusta 'recepcionesFiltradas' al nombre de tu array de filtros
    this.cdr.detectChanges();
  }

  cambiarPagina(nuevaPagina: number): void {
    if (nuevaPagina >= 1 && nuevaPagina <= this.totalPaginas) {
      this.paginaActual = nuevaPagina;
      console.log('>>> [PREFS JDE] Conmutando historial de recepciones a la página:', this.paginaActual);
      this.cdr.detectChanges();
    }
  }

  /**
   * 💡 LA COMPUERTA RECTANGULAR RECTACTIVA:
   * Recorta el array de recepciones filtradas para la vista HTML.
   */
  get recepcionesPaginadas(): any[] {
    const inicio = (this.paginaActual - 1) * this.registrosPorPagina;
    const fin = inicio + this.registrosPorPagina;
    return this.recepcionesFiltradas.slice(inicio, fin); // 💡 Ajusta 'recepcionesFiltradas' al nombre de tu array de filtros
  }  

	/**
   * ✍️ ACCIONES CONDICIONALES DEL GRID JDE
   * Abre las ventanas de firma interactiva enviando los parámetros AN8 e ID
   */
  lanzarFirmaEntrada(orden: RecepcionGrid): void {
    console.log(`📝 Iniciando Firma 1 (Recepción) para Folio: ${orden.folio}, ID Padre: ${orden.id} | Cliente AN8: ${orden.customer.an8}`);
    // Aquí invocas tu modal flotante inyectando: orden.id y orden.customer.an8
    
    // Invocación nativa mediante el MatDialog inyectado en tu constructor
    const dialogRef = this.dialog.open(SignatureDialog, {
      width: '840px',
      disableClose: true, // Evita que se cierre al dar clic afuera en el patio de servicio
      data: {
        idRecepcion: orden.id,
        folioRecepcion: orden.folio,
        an8Cliente: orden.customer.an8,
        etapaFirma: 'RECEPCION' // 🚀 INYECTADO: Le indica a la API que busque cláusulas REC_
      }
    });

    // Escucha el cierre de la ventana flotante
    dialogRef.afterClosed().subscribe((guardadoExitoso: boolean) => {
      if (guardadoExitoso === true) {
        console.log('🔄 [SaaS Grid] Rejilla de órdenes recargada por actualización de estatus.');
        this.cargarRecepciones(); // Refresca automáticamente el listado cambiando el botón por "✓ En Taller"
      }
    });

  }

  // =========================================================================
  // 💰 COMPUERTA DE FORMALIZACIÓN JURÍDICA DE PRESUPUESTOS (FIRMA 2)
  // SOLUCIÓN: Reutiliza el MatDialog enviando el identificador de etapa
  // =========================================================================
  lanzarFirmaCotizacion(orden: RecepcionGrid): void {
    console.log(`💰 Iniciando Firma 2 (Presupuesto) para Folio: ${orden.folio} | ID: ${orden.id}`);

    const dialogRef = this.dialog.open(SignatureDialog, {
      width: '840px',
      disableClose: true, // Candado de seguridad para tablets de patio
      data: {
        idRecepcion: orden.id,
        folioRecepcion: orden.folio,
        an8Cliente: orden.customer.an8,
        etapaFirma: 'COTIZACION' // 🚀 INYECTADO: Le indica a la API que busque cláusulas COT_
      }
    });

    dialogRef.afterClosed().subscribe((guardadoExitoso: boolean) => {
      if (guardadoExitoso === true) {
        console.log('🔄 [SaaS Grid] Cotización firmada con éxito. Actualizando rejilla.');
        this.cargarRecepciones(); // Recarga para transicionar el estado en frío
      }
    });
  }

  lanzarFirmaEntrega(orden: any): void {  //  RecepcionGrid
    console.log(`🚗 Iniciando Firma 3 (Liberación Final) para Folio: ${orden.folio}`);
    const dialogRef = this.dialog.open(SignatureDialog, {
      width: '840px',
      disableClose: true,
      data: {
        idRecepcion: orden.id,
        folioRecepcion: orden.folio,
        an8Cliente: orden.customer?.an8 || orden.an8Cliente,
        etapaFirma: 'ENTREGA' // 🚀 INYECTADO: Le indica a la API que guarde la rúbrica de salida
      }
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.cargarRecepciones(); // Refresca la cuadrícula en caliente
      }
    });
  }  

  // 🚀 Inyecta este método en tu archivo recepciones-list.ts
  public irAPresupuesto(idRecepcion: number): void {
    console.log(`🛰️ Transfiriendo orden ID #${idRecepcion} del patio a la rampa de cotizaciones.`);
    this.router.navigate(['/cotizaciones/add'], { queryParams: { recepcionId: idRecepcion } });
  }  

  public lanzarFirmaWorkflow(orden: any, etapa: 'RECEPCION' | 'COTIZACION' | 'ENTREGA'): void {
    console.log(`⚖️ [SaaS Legal] Abriendo SignatureDialog para Etapa: ${etapa} | Folio: ${orden.folio}`);

    const dialogRef = this.dialog.open(SignatureDialog, {
      width: '840px',
      disableClose: true,
      data: {
        idRecepcion: orden.id,
        folioRecepcion: orden.folio,
        an8Cliente: orden.customer?.an8 || orden.an8Cliente,
        etapaFirma: etapa // 🚀 Viaja de forma transparente hacia el FirmasController
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Refrescamos la cuadrícula en caliente para ver reflejados los nuevos badges de estatus
        this.cargarRecepciones(); 
      }
    });
  }

}
